import React from 'react'
import { Container, Group, Text } from '@mantine/core'

import Link from 'next/link'

import * as Types from '../declarations'
import { VersionContext } from '../contexts/VersionContext'
import { getEmailAddress } from './EmailAddress'

import UnitsSelector from './UnitsSelector'
import FeedbackWidget from './FeedbackWidget'

const data = (version: Types.VERSION) =>
	version === Types.VERSION.PEAKS
		? [
				{
					title: 'Guided Peaks',
					links: [
						{ label: 'Guides', link: '/guides' },
						{ label: 'Expeditions', link: '/expeditions' },
						{ label: 'Expedition finder', link: '/expedition-explorer' },
						{ label: 'For Guides', link: '/for-guides' },
						{ label: 'About', link: '/about' },
						{ label: 'Terms', link: '/terms' },
						{ label: 'Contact', link: `mailto:${getEmailAddress(version)}` },
					],
				},
				{
					title: 'Climbing in',
					links: [
						{ label: 'Ecuador', link: '/climbing/south-america/ecuador' },
						{ label: 'Nepal', link: '/climbing/asia/nepal' },
						{ label: 'Pakistan', link: '/climbing/asia/pakistan' },
						{ label: 'India', link: '/climbing/asia/india' },
						{ label: 'Peru', link: '/climbing/south-america/peru' },
					],
				},
				{
					title: 'Seven Summits',
					links: [
						{
							label: 'Kilimanjaro',
							link: '/climbing/africa/tanzania/kilimanjaro#guides',
						},
						{
							label: 'Aconcagua',
							link: '/climbing/south-america/argentina/aconcagua',
						},
						{ label: 'Elbrus', link: '/climbing/europe/russia/elbrus' },
						{ label: 'Everest', link: '/climbing/asia/nepal/everest' },
						{
							label: 'Denali',
							link: '/climbing/north-america/usa/denali#guides',
						},
						{
							label: 'Vinson',
							link: '/climbing/antarctica/antarctica/mount-vinson',
						},
						{
							label: 'Carstensz',
							link: '/climbing/asia/indonesia/carstensz-pyramid',
						},
					],
				},
		  ]
		: [
				{
					title: 'Guided Trekking',
					links: [
						{ label: 'Treks', link: '/expeditions' },
						{ label: 'Trek finder', link: '/expedition-explorer' },
						{ label: 'About', link: '/about' },
						{ label: 'Terms', link: '/terms' },
						{ label: 'Contact', link: `mailto:${getEmailAddress(version)}` },
					],
				},
		  ]

const BrandTitle = () => {
	// const isMobile = useIsMobile()
	const version = React.useContext(VersionContext)?.version

	return (
		<Link href="/" legacyBehavior>
			<a className="brand-title">
				<img
					src={
						version === Types.VERSION.PEAKS
							? '/branding/guided-peaks-transparent.png'
							: '/branding/guided-trekking-transparent.png'
					}
					alt={
						version === Types.VERSION.PEAKS ? 'Guided Peaks' : 'Guided Trekking'
					}
					title={
						version === Types.VERSION.PEAKS ? 'Guided Peaks' : 'Guided Trekking'
					}
					height={40}
					width={version === Types.VERSION.PEAKS ? 121 : 133}
					style={{
						objectFit: 'cover',
						borderRadius: 'var(--mantine-radius-sm)',
						width: version === Types.VERSION.PEAKS ? 121 : 133,
						height: 40,
					}}
				/>
			</a>
		</Link>
	)
}

const FooterBig = () => {
	const version = React.useContext(VersionContext).version
	const isTrekking = version === Types.VERSION.TREKKING
	const websiteName = `Guided ${isTrekking ? 'Trekking' : 'Peaks'}`

	const groups = data(version).map((group) => {
		const links = group.links.map((link, index) => (
			<Link
				key={index}
				href={link.link}
				style={{
					display: 'block',
					color: '#495057',
					fontSize: '14px',
					paddingTop: 3,
					paddingBottom: 3,
				}}
			>
				{link.label}
			</Link>
		))

		return (
			<div key={group.title} style={{ width: 160 }}>
				<Text
					style={{
						fontSize: '16px',
						fontWeight: 700,
						fontFamily: 'sans-serif',
						marginBottom: '8px',
						color: '#212529',
					}}
				>
					{group.title}
				</Text>
				{links}
			</div>
		)
	})

	return (
		<footer
			style={{
				marginTop: '60px',
				paddingTop: '40px',
				paddingBottom: '40px',
				backgroundColor: '#f8f9fa',
				borderTop: '1px solid #dee2e6',
			}}
		>
			<Container
				size="xl"
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					flexWrap: 'wrap',
					maxWidth: '1100px',
				}}
			>
				<div style={{ maxWidth: 200 }}>
					<BrandTitle />
					<Text
						size="xs"
						style={{
							marginTop: 5,
							color: '#868e96',
							fontSize: '12px',
						}}
					>
						Connect directly with the best guides from around the world.
					</Text>
				</div>

				<div
					style={{
						display: 'flex',
						flexWrap: 'wrap',
						gap: '40px',
						marginTop: '20px',
					}}
				>
					{groups}
				</div>
			</Container>

			<Container
				size="xl"
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginTop: '32px',
					paddingTop: '32px',
					paddingBottom: '32px',
					borderTop: '1px solid #dee2e6',
					flexWrap: 'wrap',
					maxWidth: '1100px',
				}}
			>
				<Text size="sm" style={{ color: '#868e96', fontSize: '14px' }}>
					&copy; {new Date().getFullYear()} {websiteName}. All rights reserved,
					except where otherwise noted.
				</Text>
				<Group gap="xs" wrap="nowrap">
					<UnitsSelector />
					<FeedbackWidget version={version} />
				</Group>
			</Container>
		</footer>
	)
}

export default FooterBig
